import { API_URL, fetchWithHandling } from './api';

// ---------------------------------------------------------------------------
// Skills API Functions
//
// Same shape as reportTemplateApi.js: fetchWithHandling, Authorization:
// Bearer <accessToken> (accessToken here is really the current project's
// token — see DataProvider.js / App.js's projectToken flow — so a skill
// created through this module is scoped to "the project I'm currently in",
// matching how report templates and DOCX templates already behave).
//
// GET /list returns system skills (read-only, is_system: true) UNION this
// project's own skills — the UI should only offer Edit/Toggle/Delete for
// rows where is_system is false.
//
// Skills use the open Agent Skills format (agentskills.io), same as
// github.com/anthropics/skills and github.com/openai/skills: a folder with a
// SKILL.md plus optional references/, scripts/, assets/, agents/openai.yaml.
// importSkill() uploads one as-is; the backend does all parsing/validation.
// ---------------------------------------------------------------------------

/**
 * List available tools a skill may reference (the fixed, existing tool
 * catalog — never hardcode this list in the frontend).
 */
export async function listAvailableSkillTools(accessToken) {
  return fetchWithHandling(`${API_URL}/data/skills/available-tools`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * List all skills visible to the current project (system + this project's).
 */
export async function listSkills(accessToken) {
  return fetchWithHandling(`${API_URL}/data/skills/list`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * Create a project-scoped skill. `toolNames` must be a subset of the names
 * returned by listAvailableSkillTools — the backend re-validates this too.
 */
export async function createSkill(
  accessToken,
  { slug, name, description = '', instructions, toolNames = [] },
) {
  return fetchWithHandling(`${API_URL}/data/skills/create`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify({
      slug,
      name,
      description,
      instructions,
      tool_names: toolNames,
    }),
  });
}

/**
 * Update a project-scoped skill. Only pass the fields you want to change.
 */
export async function updateSkill(accessToken, skillId, updates) {
  const body = {};
  if (updates.name !== undefined) body.name = updates.name;
  if (updates.description !== undefined) body.description = updates.description;
  if (updates.instructions !== undefined)
    body.instructions = updates.instructions;
  if (updates.toolNames !== undefined) body.tool_names = updates.toolNames;
  if (updates.license !== undefined) body.license = updates.license;
  if (updates.compatibility !== undefined)
    body.compatibility = updates.compatibility;
  if (updates.metadata !== undefined) body.metadata = updates.metadata;

  return fetchWithHandling(`${API_URL}/data/skills/update/${skillId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body),
  });
}

/**
 * Toggle a project-scoped skill's active/inactive status.
 */
export async function toggleSkillStatus(accessToken, skillId) {
  return fetchWithHandling(`${API_URL}/data/skills/toggle-status/${skillId}`, {
    method: 'PATCH',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * Delete a project-scoped skill.
 */
export async function deleteSkill(accessToken, skillId) {
  return fetchWithHandling(`${API_URL}/data/skills/delete/${skillId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
  });
}

/**
 * Import a skill in the Agent Skills format. `files` is either one
 * .zip/.skill archive, one SKILL.md, or every file of a folder picked with
 * <input webkitdirectory> (their relative paths are sent alongside, since
 * the multipart filename alone loses the folder structure).
 */
export async function importSkill(accessToken, files) {
  const formData = new FormData();
  files.forEach((file) => {
    formData.append('files', file);
    if (files.length > 1) {
      formData.append('paths', file.webkitRelativePath || file.name);
    }
  });
  // No Content-Type header: the browser must set the multipart boundary.
  return fetchWithHandling(`${API_URL}/data/skills/import`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: formData,
  });
}

/**
 * Text preview of one file bundled with a skill. Returned as JSON
 * ({ path, is_text, size, truncated, content }) -- never rendered as HTML.
 */
export async function getSkillFileContent(accessToken, skillId, path) {
  const params = new URLSearchParams({ path });
  return fetchWithHandling(
    `${API_URL}/data/skills/${skillId}/files/content?${params.toString()}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
}

/**
 * Import a skill from explicit (path, blob) entries -- how the editor
 * creates a skill: it builds SKILL.md + bundled files client-side and lets
 * the backend parse/validate them exactly like an uploaded folder.
 */
export async function importSkillEntries(accessToken, entries) {
  const formData = new FormData();
  entries.forEach(({ path, blob }) => {
    formData.append('files', blob, path.split('/').pop());
    formData.append('paths', path);
  });
  return fetchWithHandling(`${API_URL}/data/skills/import`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: formData,
  });
}

/**
 * Add or replace bundled files of a skill. `entries`: [{ path, blob }] with
 * paths inside the skill folder, e.g. "references/guide.md".
 */
export async function putSkillFiles(accessToken, skillId, entries) {
  const formData = new FormData();
  entries.forEach(({ path, blob }) => {
    formData.append('files', blob, path.split('/').pop());
    formData.append('paths', path);
  });
  return fetchWithHandling(`${API_URL}/data/skills/${skillId}/files`, {
    method: 'PUT',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: formData,
  });
}

/** Remove one bundled file from a skill. */
export async function deleteSkillFile(accessToken, skillId, path) {
  const params = new URLSearchParams({ path });
  return fetchWithHandling(
    `${API_URL}/data/skills/${skillId}/files?${params.toString()}`,
    {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${accessToken}`,
      },
    },
  );
}

// fetchWithHandling parses JSON; zips need the raw response.
async function downloadFile(url, accessToken, filename) {
  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${accessToken}` },
    credentials: 'include',
  });
  if (!response.ok) {
    const details = await response.json().catch(() => ({}));
    throw (
      details.error || details.detail || `Download failed (${response.status})`
    );
  }
  const blobUrl = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(blobUrl);
}

/** A bundled .docx as a Blob, for the in-page preview. */
export async function getSkillDocx(accessToken, skillId, path) {
  const params = new URLSearchParams({ path });
  const response = await fetch(
    `${API_URL}/data/skills/${skillId}/files/docx?${params.toString()}`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
      credentials: 'include',
    },
  );
  if (!response.ok)
    throw new Error(`Could not load ${path} (${response.status})`);
  return response.blob();
}

/** Starter skill folder (SKILL.md, references/, scripts/, assets/) as a zip. */
export async function downloadSkillTemplateZip(accessToken) {
  return downloadFile(
    `${API_URL}/data/skills/template.zip`,
    accessToken,
    'my-skill.zip',
  );
}

/** Download a skill as a folder zip (re-importable, editable offline). */
export async function exportSkill(accessToken, skillId, slug) {
  return downloadFile(
    `${API_URL}/data/skills/${skillId}/export.zip`,
    accessToken,
    `${slug || 'skill'}.zip`,
  );
}
